import {
    ChevronLeft,
    ChevronRight,
    Close,
    Download,
} from "@mui/icons-material";
import {
    Box,
    CircularProgress,
    Dialog,
    IconButton,
    Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { MultiPermissionAuthorize } from "../../../Utilities/MultiPermissionAuthorize";
import { S3Service } from "../../../Utilities/S3Service";
import { monthFormatter } from "../Bill/SingleBill";
import { numberFormatter } from "../Bill/Cells/Last12Summary";
import { downloadReadingImageFile } from "./Reading";

type ReadingImageFullscreenProps = {
    open: boolean;
    rows: any[];
    activeIndex: number | null;
    onClose: () => void;
    onIndexChange: (index: number) => void;
};

const ReadingImageThumbnail = ({
    reading,
    isActive,
    onClick,
    thumbnailRef,
}: {
    reading: any;
    isActive: boolean;
    onClick: () => void;
    thumbnailRef: (el: HTMLDivElement | null) => void;
}) => {
    const [url, setUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let objectUrl: string | null = null;
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            const [data, err] = await S3Service.getS3ByKey(
                `readings/${reading.id}`
            );

            if (!data || err || cancelled) {
                setLoading(false);
                return;
            }

            try {
                const response = await fetch(data.url);
                if (response.status === 200 && !cancelled) {
                    const blob = await response.blob();
                    objectUrl = URL.createObjectURL(blob);
                    setUrl(objectUrl);
                }
            } catch {
                // no image
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [reading.id]);

    return (
        <Box
            ref={thumbnailRef}
            onClick={onClick}
            sx={{
                flexShrink: 0,
                width: 72,
                height: 54,
                borderRadius: 1,
                overflow: "hidden",
                cursor: "pointer",
                border: isActive ? "2px solid white" : "2px solid transparent",
                opacity: isActive ? 1 : 0.6,
                transition: "opacity 0.2s, border-color 0.2s",
                "&:hover": { opacity: 1 },
                bgcolor: "rgba(255,255,255,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            {loading ? (
                <CircularProgress size={16} sx={{ color: "white" }} />
            ) : url ? (
                <Box
                    component="img"
                    src={url}
                    alt={reading.meter?.meterNo}
                    sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                    }}
                />
            ) : (
                <Typography variant="caption" color="grey.500" fontSize={9}>
                    N/A
                </Typography>
            )}
        </Box>
    );
};

export const ReadingImageFullscreen = ({
    open,
    rows,
    activeIndex,
    onClose,
    onIndexChange,
}: ReadingImageFullscreenProps) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const imageUrlRef = useRef<string | null>(null);
    const thumbnailStripRef = useRef<HTMLDivElement>(null);
    const thumbnailRefs = useRef<Record<number, HTMLDivElement | null>>({});
    const scrollCooldown = useRef(false);

    const activeReading =
        activeIndex !== null ? rows[activeIndex] : null;

    const loadMainImage = useCallback(async (index: number) => {
        const reading = rows[index];
        if (!reading) return;

        if (imageUrlRef.current) {
            URL.revokeObjectURL(imageUrlRef.current);
            imageUrlRef.current = null;
        }

        setLoading(true);
        setImageUrl(null);

        const [s3Data, err] = await S3Service.getS3ByKey(
            `readings/${reading.id}`
        );

        if (!s3Data || err) {
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(s3Data.url);
            if (response.status === 200) {
                const blob = await response.blob();
                const url = URL.createObjectURL(blob);
                imageUrlRef.current = url;
                setImageUrl(url);
            }
        } catch {
            setImageUrl(null);
        } finally {
            setLoading(false);
        }
    }, [rows]);

    const navigate = useCallback(
        (direction: -1 | 1) => {
            if (activeIndex === null) return;
            const next = activeIndex + direction;
            if (next < 0 || next >= rows.length) return;
            onIndexChange(next);
        },
        [activeIndex, rows.length, onIndexChange]
    );

    useEffect(() => {
        if (!open || activeIndex === null) return;
        loadMainImage(activeIndex);
    }, [open, activeIndex, loadMainImage]);

    useEffect(() => {
        return () => {
            if (imageUrlRef.current) {
                URL.revokeObjectURL(imageUrlRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (activeIndex === null || !open) return;
        const reading = rows[activeIndex];
        const el = thumbnailRefs.current[reading?.id];
        el?.scrollIntoView({
            behavior: "smooth",
            inline: "center",
            block: "nearest",
        });
    }, [activeIndex, open, rows]);

    useEffect(() => {
        if (!open) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "ArrowRight") navigate(1);
            else if (e.key === "ArrowLeft") navigate(-1);
            else if (e.key === "Escape") onClose();
        };

        const handleWheel = (e: WheelEvent) => {
            if (scrollCooldown.current) return;
            e.preventDefault();

            if (e.deltaY > 30) navigate(1);
            else if (e.deltaY < -30) navigate(-1);
            else return;

            scrollCooldown.current = true;
            setTimeout(() => {
                scrollCooldown.current = false;
            }, 300);
        };

        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("wheel", handleWheel, { passive: false });

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("wheel", handleWheel);
        };
    }, [open, navigate, onClose]);

    const handleClose = () => {
        if (imageUrlRef.current) {
            URL.revokeObjectURL(imageUrlRef.current);
            imageUrlRef.current = null;
        }
        setImageUrl(null);
        onClose();
    };

    return (
        <Dialog fullScreen open={open} onClose={handleClose}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    bgcolor: "black",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                        py: 1,
                        bgcolor: "rgba(0,0,0,0.8)",
                        flexShrink: 0,
                    }}
                >
                    <Box>
                        <Typography color="white" variant="h6">
                            {activeReading?.meter?.meterNo ?? ""}
                            {activeReading
                                ? ` — ${monthFormatter.format(
                                      new Date(
                                          activeReading.year ?? 0,
                                          (activeReading.month ?? 1) - 1,
                                          1
                                      )
                                  )} — Curr. Units: ${
                                      activeReading.currentUnits != null
                                          ? numberFormatter.format(
                                                activeReading.currentUnits
                                            )
                                          : "—"
                                  }`
                                : ""}
                        </Typography>
                        {activeIndex !== null && (
                            <Typography variant="caption" color="grey.400">
                                {activeIndex + 1} of {rows.length}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {activeReading && (
                            <MultiPermissionAuthorize
                                ops={["DOWNLOAD READING_IMAGE"]}
                            >
                                <IconButton
                                    onClick={() =>
                                        downloadReadingImageFile(activeReading)
                                    }
                                    sx={{ color: "white" }}
                                >
                                    <Download />
                                </IconButton>
                            </MultiPermissionAuthorize>
                        )}
                        <IconButton onClick={handleClose} sx={{ color: "white" }}>
                            <Close />
                        </IconButton>
                    </Box>
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        minHeight: 0,
                    }}
                >
                    {activeIndex !== null && activeIndex > 0 && (
                        <IconButton
                            onClick={() => navigate(-1)}
                            sx={{
                                position: "absolute",
                                left: 16,
                                zIndex: 1,
                                color: "white",
                                bgcolor: "rgba(255,255,255,0.15)",
                                "&:hover": {
                                    bgcolor: "rgba(255,255,255,0.25)",
                                },
                            }}
                        >
                            <ChevronLeft fontSize="large" />
                        </IconButton>
                    )}

                    {loading ? (
                        <CircularProgress sx={{ color: "white" }} />
                    ) : imageUrl ? (
                        <Box
                            component="img"
                            src={imageUrl}
                            alt="Reading"
                            sx={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                                px: 8,
                            }}
                        />
                    ) : (
                        <Typography color="white">No image uploaded</Typography>
                    )}

                    {activeIndex !== null && activeIndex < rows.length - 1 && (
                        <IconButton
                            onClick={() => navigate(1)}
                            sx={{
                                position: "absolute",
                                right: 16,
                                zIndex: 1,
                                color: "white",
                                bgcolor: "rgba(255,255,255,0.15)",
                                "&:hover": {
                                    bgcolor: "rgba(255,255,255,0.25)",
                                },
                            }}
                        >
                            <ChevronRight fontSize="large" />
                        </IconButton>
                    )}
                </Box>

                {rows.length > 1 && (
                    <Box
                        ref={thumbnailStripRef}
                        sx={{
                            flexShrink: 0,
                            display: "flex",
                            gap: 1,
                            px: 2,
                            py: 1.5,
                            overflowX: "auto",
                            bgcolor: "rgba(0,0,0,0.85)",
                            "&::-webkit-scrollbar": { height: 6 },
                            "&::-webkit-scrollbar-thumb": {
                                bgcolor: "rgba(255,255,255,0.3)",
                                borderRadius: 3,
                            },
                        }}
                    >
                        {rows.map((row, index) => (
                            <ReadingImageThumbnail
                                key={row.id}
                                reading={row}
                                isActive={index === activeIndex}
                                onClick={() => onIndexChange(index)}
                                thumbnailRef={(el) => {
                                    thumbnailRefs.current[row.id] = el;
                                }}
                            />
                        ))}
                    </Box>
                )}
            </Box>
        </Dialog>
    );
};
